import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Spinner, ProtectedRoute } from "@/components/common";
import api from "@/lib/api";
import { safeInternalPath } from "@/pages/auth/authShared";

import Home from "@/pages/Home";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import DanceLibrary from "@/pages/DanceLibrary";
import DanceDetail from "@/pages/DanceDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import NewDancers from "@/pages/NewDancers";
import BasicSteps from "@/pages/BasicSteps";
import DanceFloorEtiquette from "@/pages/DanceFloorEtiquette";
import Instructors from "@/pages/Instructors";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import MemberDashboard from "@/pages/member/MemberDashboard";
import MyClasses from "@/pages/member/MyClasses";
import MyDances from "@/pages/member/MyDances";
import WhatsOn from "@/pages/member/WhatsOn";
import Account from "@/pages/member/Account";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ManageDances from "@/pages/admin/ManageDances";
import ManageClasses from "@/pages/admin/ManageClasses";
import ManageVenues from "@/pages/admin/ManageVenues";
import ManageEvents from "@/pages/admin/ManageEvents";
import ManageSubmissions from "@/pages/admin/ManageSubmissions";
import ManageInstructors from "@/pages/admin/ManageInstructors";
import ManageAnnouncements from "@/pages/admin/ManageAnnouncements";
import ManageSMS from "@/pages/admin/ManageSMS";
import ManageAssignments from "@/pages/admin/ManageAssignments";
import ClassSessions from "@/pages/admin/ClassSessions";
import ManageUsers from "@/pages/admin/ManageUsers";
import MemberSafety from "@/pages/admin/MemberSafety";
import PermissionAudit from "@/pages/admin/PermissionAudit";
import ClickSendReconciliation from "@/pages/admin/ClickSendReconciliation";
import UnifiedSessions from "@/pages/admin/UnifiedSessions";
import UnifiedPreview from "@/pages/admin/UnifiedPreview";

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", "?"));
    const sid = params.get("session_id");
    const next = safeInternalPath(new URLSearchParams(window.location.search).get("next"));
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data);
        window.history.replaceState(null, "", next);
        navigate(next, { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Spinner label="Signing you in…" />;
}

const page = (el) => <Layout>{el}</Layout>;
const guard = (el, staffOnly = false, adminOnly = false) => <ProtectedRoute staffOnly={staffOnly} adminOnly={adminOnly}><Layout>{el}</Layout></ProtectedRoute>;

function AppShell() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={page(<Home />)} />
      <Route path="/classes" element={page(<Classes />)} />
      <Route path="/classes/:slug" element={page(<ClassDetail />)} />
      <Route path="/dances" element={page(<DanceLibrary />)} />
      <Route path="/dances/:slug" element={page(<DanceDetail />)} />
      <Route path="/events" element={page(<Events />)} />
      <Route path="/events/:slug" element={page(<EventDetail />)} />
      <Route path="/new-dancers" element={page(<NewDancers />)} />
      <Route path="/basic-steps" element={page(<BasicSteps />)} />
      <Route path="/dance-floor-etiquette" element={page(<DanceFloorEtiquette />)} />
      <Route path="/instructors" element={page(<Instructors />)} />
      <Route path="/about" element={page(<About />)} />
      <Route path="/contact" element={page(<Contact />)} />
      <Route path="/privacy" element={page(<Privacy />)} />
      <Route path="/login" element={page(<Login />)} />
      <Route path="/register" element={page(<Register />)} />
      <Route path="/forgot-password" element={page(<ForgotPassword />)} />
      <Route path="/reset-password" element={page(<ResetPassword />)} />
      <Route path="/dashboard" element={guard(<MemberDashboard />)} />
      <Route path="/my-classes" element={guard(<MyClasses />)} />
      <Route path="/my-dances" element={guard(<MyDances />)} />
      <Route path="/whats-on" element={page(<WhatsOn />)} />
      <Route path="/account" element={guard(<Account />)} />
      <Route path="/admin" element={guard(<AdminDashboard />, true)} />
      <Route path="/admin/dances" element={guard(<ManageDances />, true)} />
      <Route path="/admin/classes" element={guard(<ManageClasses />, true, true)} />
      <Route path="/admin/venues" element={guard(<ManageVenues />, true, true)} />
      <Route path="/admin/events" element={guard(<ManageEvents />, true, true)} />
      <Route path="/admin/submissions" element={guard(<ManageSubmissions />, true, true)} />
      <Route path="/admin/instructors" element={guard(<ManageInstructors />, true, true)} />
      <Route path="/admin/announcements" element={guard(<ManageAnnouncements />, true)} />
      <Route path="/admin/assignments" element={guard(<ManageAssignments />, true)} />
      <Route path="/admin/sessions" element={guard(<ClassSessions />, true)} />
      <Route path="/admin/unified-sessions" element={guard(<UnifiedSessions />, true)} />
      <Route path="/admin/unified-preview" element={guard(<UnifiedPreview />, true, true)} />
      <Route path="/admin/users" element={guard(<ManageUsers />, true, true)} />
      <Route path="/admin/safety" element={guard(<MemberSafety />, true)} />
      <Route path="/admin/permission-audit" element={guard(<PermissionAudit />, true, true)} />
      <Route path="/admin/reconciliation" element={guard(<ClickSendReconciliation />, true)} />
      <Route path="/admin/sms" element={guard(<ManageSMS />, true)} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
